import { Application, BasePlugin } from '@goodboydigital/astro';
import SoundBoy from '@goodboydigital/soundboy';
import { Signal } from 'typed-signals';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { ShopIconSizes } from '../ui/ShopIcon';
import { getMostSuitableLanguage, isLanguageAvailable, LanguageID } from '../ui/TextConfig';
import { Browser } from '../utils/Browser';

/**
 * Manages the purchased/unlocked states and prices for items
 */
export class SettingsPlugin implements BasePlugin
{
    public app: App;

    public muteChanged: Signal<(state: boolean) => void>;
    public languageChanged: Signal<(newLanguage: LanguageID) => void>;
    public shopIconSizeChanged: Signal<(size: ShopIconSizes) => void>;
    public accessibilityChanged: Signal<(state: boolean) => void>;

    private _shopIconSize: ShopIconSizes;
    private _accessibilityEnabled: boolean;
    private _playerName: string | undefined;
    private _muteState: boolean;
    private _hasPlayerSetName: boolean;

    constructor(app: Application)
    {
        this.app = app as App;
        this.muteChanged = new Signal<(state: boolean) => void>();
        this.languageChanged = new Signal<(newLanguage: LanguageID) => void>();
        this.accessibilityChanged = new Signal<(state: boolean) => void>();
        this.shopIconSizeChanged = new Signal<() => void>();

        // TODO: Load default settings from storage
        this._shopIconSize = 'large';
        this._accessibilityEnabled = false;
        this._hasPlayerSetName = this.app.user.data.settings.hasPlayerSetName || false;

        this._playerName = this.app?.user?.data?.vehicle.nickname || undefined;

        this._muteState = this.app?.user?.data?.settings.mute || false;
        SoundBoy.muted = this._muteState;
        this.muteChanged.emit(this._muteState);

        if(!Settings.isApp() && !Settings.language){
            var indexLang = document.documentElement.lang;
            if(indexLang && indexLang != "_LANG_"){
                this.language = getMostSuitableLanguage(indexLang);
            }
            else{
                this.autoSelectLanguage();
            }
            
        }
        
    }

    public autoSelectLanguage(): void
    {
        const available = isLanguageAvailable(this.language);

        if (available) return;

        this.language = getMostSuitableLanguage(Browser.getSystemLanguage());
    }

    public update(): void
    {
        // Only to make a valid SystemInterface
    }

    public toggleMuted(): void
    {
        this._muteState = !SoundBoy.muted;
        SoundBoy.muted = this._muteState;
        this.muteChanged.emit(this._muteState);

        this.app.user.data.settings.mute = this._muteState;
        Shared.game.garage.user.save();
    }

    public get muted(): boolean { return SoundBoy.muted; }
    public set muted(state: boolean)
    {
        this._muteState = state;

        SoundBoy.muted = this._muteState;
        this.muteChanged.emit(this._muteState);

        this.app.user.data.settings.mute = this._muteState;
        Shared.game.garage.user.save();
    }

    get shopIconSize(): ShopIconSizes { return this._shopIconSize; }
    set shopIconSize(val: ShopIconSizes)
    {
        this._shopIconSize = val;
        this.shopIconSizeChanged.emit(val);
    }

    get accessible(): boolean { return this._accessibilityEnabled; }
    set accessible(val: boolean)
    {
        this._accessibilityEnabled = val;
        this.accessibilityChanged.emit(val);
    }

    get playerName(): string
    {
        return this._playerName ? this._playerName : Shared.game.garage?.user?.data?.vehicle.nickname || 'AAA';
    }

    set playerName(val: string)
    {
        this._playerName = val;
        this.app.user.data.vehicle.nickname = val;
        this.app.user.save();
        if (Shared.game)
        {
            if(Shared.game.vehicle.tag){
                Shared.game.vehicle.tag.setNickname(val);
            }
            
            Shared.game.garage.updateColor();
        }
    }
    
    get hasPlayerSetName(): boolean
    {
        return this._hasPlayerSetName;
    }

    set hasPlayerSetName(val: boolean)
    {
        this._hasPlayerSetName = val;
        this.app.user.data.settings.hasPlayerSetName = val;
        this.app.user.save();
    }

    get playerFlag(): string
    {
        return this.app.user.data.vehicle.flag;
    }

    set playerFlag(val: string)
    {
        this.app.user.data.vehicle.flag = val;
        this.app.user.save();
        if (Shared.game) 
        {
            if(Shared.game.vehicle.tag){
                Shared.game.vehicle.tag.setFlag(val);
            }
        }
    }

    get language(): LanguageID { return this.app.user.data.settings.language as LanguageID; }
    set language(val: LanguageID)
    {
        if(this.app.user.data.settings.language == val) return;

        this.app.user.data.settings.language = val;
        
        this.languageChanged.emit(val);
        this.app.user.save();
        
    }

    get termsAccepted(): boolean { return this.app.user.data.settings.termsAccepted; }
    set termsAccepted(val: boolean)
    {
        this.app.user.data.settings.termsAccepted = val;
        this.app.user.save();
    }
}
